import { useEffect, useMemo, useRef, useState } from 'react'
import { X, Pencil, Undo2, Trash2, Search, Loader2, Upload, FileUp } from 'lucide-react'
import area from '@turf/area'
import { useAtlasStore } from '@/stores/atlasStore'
import { projectsService } from '@core/projects'
import { ordersService } from '@core/orders'
import { productsService, calculateProductPrice, type Product } from '@core/products'
import { parseGeometryFile } from '@core/geometry'
import { PAYMENTS_ENABLED } from '@core/billing'
import { ProductPanel } from './ProductPanel'
import { CheckoutPanel } from './CheckoutPanel'
import { searchPlaces, type GeoResult } from '@/lib/geocode'

type GeomMode = 'draw' | 'upload'
const ACCEPT = '.geojson,.json,.kml,.kmz,.zip,.dxf'

// Full navigation (not client-side) so the viewer re-bootstraps cleanly into
// the freshly created order. Matches the ProjectSwitcher order-select pattern.
function orderUrl(id: string): string {
  return window.location.origin + import.meta.env.BASE_URL + '?order=' + id
}

/**
 * In-map "novo pedido" panel (Phase 4, 4o-d). A right-side surface over the
 * empty atlas map: name + polygon draw (vertices captured on the map via the
 * atlasStore draft slice + MapView2D) + the layer picker fed the live drawn
 * area, then places the order (createProject -> createOrder -> view).
 *
 * Self-gates on createMode so App can render it unconditionally.
 */
export function OrderPanel() {
  const createMode = useAtlasStore((s) => s.createMode)
  const drawing = useAtlasStore((s) => s.drawing)
  const draftPoints = useAtlasStore((s) => s.draftPoints)
  const draftGeometry = useAtlasStore((s) => s.draftGeometry)
  const setCreateMode = useAtlasStore((s) => s.setCreateMode)
  const setDrawing = useAtlasStore((s) => s.setDrawing)
  const undoDraftPoint = useAtlasStore((s) => s.undoDraftPoint)
  const clearDraft = useAtlasStore((s) => s.clearDraft)
  const setDraftGeometry = useAtlasStore((s) => s.setDraftGeometry)

  const [name, setName] = useState('')
  const [products, setProducts] = useState<Product[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // When payments are enabled, a created order moves into checkout instead of
  // navigating straight to the (now unpaid, not-yet-processing) order view.
  const [checkout, setCheckout] = useState<{ orderId: string; netEur: number; count: number } | null>(null)

  // Geometry input mode: draw on the map, or upload a polygon file.
  const [geomMode, setGeomMode] = useState<GeomMode>('draw')
  const [parsing, setParsing] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadNotice, setUploadNotice] = useState<string | null>(null)
  const [uploadedName, setUploadedName] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleFile = async (file: File | null | undefined) => {
    if (!file || parsing) return
    setParsing(true)
    setUploadError(null)
    setUploadNotice(null)
    try {
      const result = await parseGeometryFile(file)
      setDraftGeometry(result.polygon)
      setUploadedName(file.name)
      if (result.polygonCount > 1) {
        setUploadNotice(
          `O ficheiro continha ${result.polygonCount} poligonos; usamos o maior (${result.areaHa.toFixed(2)} ha). Se nao for o pretendido, carregue um ficheiro com apenas um poligono.`,
        )
      }
      const map = (window as any).__maplibreMap
      if (map) {
        map.fitBounds(
          [[result.bbox[0], result.bbox[1]], [result.bbox[2], result.bbox[3]]],
          { padding: 60, maxZoom: 17, duration: 1000 },
        )
      }
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Erro ao processar o ficheiro')
      setUploadedName(file.name)
    } finally {
      setParsing(false)
    }
  }

  const clearUpload = () => {
    clearDraft()
    setUploadedName(null)
    setUploadError(null)
    setUploadNotice(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Search-to-navigate (Phase A: postal code + city/place via geocode).
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<GeoResult[]>([])
  const [searching, setSearching] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const runSearch = (q: string) => {
    setQuery(q)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (q.trim().length < 3) { setResults([]); setSearching(false); return }
    setSearching(true)
    debounceRef.current = setTimeout(async () => {
      const r = await searchPlaces(q)
      setResults(r)
      setSearching(false)
    }, 400)
  }

  const goTo = (r: GeoResult) => {
    const map = (window as any).__maplibreMap
    if (map) {
      if (r.bbox) {
        map.fitBounds([[r.bbox[0], r.bbox[1]], [r.bbox[2], r.bbox[3]]], { padding: 60, maxZoom: 17, duration: 1200 })
      } else {
        map.flyTo({ center: [r.lon, r.lat], zoom: r.kind === 'postal' ? 15 : 14, duration: 1200 })
      }
    }
    setResults([])
    setQuery(r.label)
  }

  // Load products once for building order items (the picker fetches its own
  // copy for display; this is for the createOrder payload).
  useEffect(() => {
    if (!createMode) return
    let cancelled = false
    productsService.getProducts().then(({ products: p }) => {
      if (!cancelled) setProducts(p)
    })
    return () => { cancelled = true }
  }, [createMode])

  const draftPolygon = useMemo<GeoJSON.Polygon | null>(() => {
    if (draftGeometry) return draftGeometry
    if (draftPoints.length < 3) return null
    return { type: 'Polygon', coordinates: [[...draftPoints, draftPoints[0]]] }
  }, [draftGeometry, draftPoints])

  const areaHa = useMemo(() => {
    if (!draftPolygon) return 0
    return area(draftPolygon) / 10000
  }, [draftPolygon])

  if (!createMode) return null

  if (checkout) {
    return (
      <CheckoutPanel
        orderId={checkout.orderId}
        netEur={checkout.netEur}
        productCount={checkout.count}
        onCancel={() => window.location.assign(orderUrl(checkout.orderId))}
      />
    )
  }

  const close = () => {
    setCreateMode(false)
    setName('')
    setError(null)
  }

  const placeOrder = async (codes: string[], total: number) => {
    if (!draftPolygon || !name.trim() || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const { project, error: pe } = await projectsService.createProject({
        name: name.trim(),
        polygon: draftPolygon,
      })
      if (pe || !project) {
        setError(pe?.message ?? 'Erro ao criar projeto')
        return
      }
      const items = codes.map((code) => {
        const p = products.find((x) => x.code === code)
        const priceCents = p ? Math.round(calculateProductPrice(p, areaHa) * 100) : 0
        return { product_type: code, polygon: draftPolygon, area_ha: areaHa, price_cents: priceCents }
      })
      const { order, error: oe } = await ordersService.createOrder({
        items,
        total_cents: Math.round(total * 100),
        project_id: project.id,
      })
      if (oe || !order) {
        setError(oe?.message ?? 'Erro ao criar pedido')
        return
      }
      if (PAYMENTS_ENABLED) {
        // Order created unpaid; move to checkout (Stripe) before processing.
        setCheckout({ orderId: order.id, netEur: total, count: codes.length })
        return
      }
      window.location.assign(orderUrl(order.id))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro inesperado')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="absolute top-14 right-0 z-20 h-[calc(100%-3.5rem)] w-[360px] max-w-full
                 bg-basalt/90 backdrop-blur-md border-l border-white/[0.1]
                 flex flex-col text-limestone"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-12 border-b border-white/[0.08] shrink-0">
        <span className="text-sm font-medium">Novo pedido</span>
        <button
          type="button"
          onClick={close}
          className="text-clay hover:text-limestone p-1"
          aria-label="Fechar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Search to navigate (postal code / city / freguesia / address) */}
        <div className="relative">
          <label className="block text-xs mb-1 text-clay">Procurar local</label>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-clay pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => runSearch(e.target.value)}
              placeholder="código postal, cidade, freguesia, morada"
              className="w-full rounded-lg pl-8 pr-8 py-2 text-sm bg-schist border border-white/[0.1] outline-none focus:border-terracotta/60"
            />
            {searching && <Loader2 className="absolute right-2 top-2.5 w-4 h-4 text-clay animate-spin" />}
          </div>
          {results.length > 0 && (
            <ul className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto rounded-lg bg-basalt border border-white/[0.12] shadow-xl">
              {results.map((r, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => goTo(r)}
                    className="w-full text-left px-3 py-2 text-xs text-limestone hover:bg-schist/70 border-b border-white/[0.05] last:border-0"
                  >
                    {r.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Name */}
        <div>
          <label className="block text-xs mb-1 text-clay">Nome do projeto</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex: Quinta da Vinha"
            className="w-full rounded-lg px-3 py-2 text-sm bg-schist border border-white/[0.1] outline-none focus:border-terracotta/60"
          />
        </div>

        {/* Geometry: draw on the map or upload a polygon file */}
        <div className="rounded-lg border border-white/[0.1] bg-white/[0.04] p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-clay">Terreno</span>
            {areaHa > 0 && (
              <span className="text-xs font-medium text-limestone">{areaHa.toFixed(2)} ha</span>
            )}
          </div>

          {/* Mode toggle */}
          <div className="flex rounded-lg border border-white/[0.1] overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setGeomMode('draw')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 ${geomMode === 'draw' ? 'bg-terracotta text-limestone' : 'text-clay hover:text-limestone'}`}
            >
              <Pencil className="w-3.5 h-3.5" /> Desenhar
            </button>
            <button
              type="button"
              onClick={() => setGeomMode('upload')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 ${geomMode === 'upload' ? 'bg-terracotta text-limestone' : 'text-clay hover:text-limestone'}`}
            >
              <Upload className="w-3.5 h-3.5" /> Carregar ficheiro
            </button>
          </div>

          {/* DRAW mode */}
          {geomMode === 'draw' && (
            <>
              {!drawing && draftPoints.length === 0 && (
                <button
                  type="button"
                  onClick={() => { clearDraft(); setDrawing(true) }}
                  className="w-full flex items-center justify-center gap-2 rounded-lg border border-terracotta/50 text-terracotta hover:bg-terracotta/10 px-3 py-2 text-sm"
                >
                  <Pencil className="w-4 h-4" /> Desenhar no mapa
                </button>
              )}

              {drawing && (
                <>
                  <p className="text-xs text-clay">
                    Clique no mapa para adicionar vertices ({draftPoints.length}). Minimo 3.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setDrawing(false)}
                      disabled={draftPoints.length < 3}
                      className="flex-1 rounded-lg bg-terracotta text-limestone px-3 py-2 text-sm disabled:opacity-40"
                    >
                      Concluir
                    </button>
                    <button
                      type="button"
                      onClick={undoDraftPoint}
                      disabled={draftPoints.length === 0}
                      className="rounded-lg border border-white/[0.1] px-3 py-2 text-clay hover:text-limestone disabled:opacity-40"
                      aria-label="Desfazer"
                    >
                      <Undo2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={clearDraft}
                      className="rounded-lg border border-white/[0.1] px-3 py-2 text-clay hover:text-limestone"
                      aria-label="Limpar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}

              {!drawing && draftPoints.length > 0 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setDrawing(true)}
                    className="flex-1 rounded-lg border border-white/[0.1] text-clay hover:text-limestone px-3 py-2 text-sm"
                  >
                    Continuar desenho
                  </button>
                  <button
                    type="button"
                    onClick={clearDraft}
                    className="rounded-lg border border-white/[0.1] px-3 py-2 text-clay hover:text-limestone"
                    aria-label="Limpar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}

          {/* UPLOAD mode */}
          {geomMode === 'upload' && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />

              {!draftGeometry && (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]) }}
                  className={`cursor-pointer rounded-lg border border-dashed px-3 py-6 text-center text-xs ${dragOver ? 'border-terracotta bg-terracotta/10 text-limestone' : 'border-white/[0.2] text-clay hover:border-terracotta/50'}`}
                >
                  {parsing ? (
                    <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> A processar...</span>
                  ) : (
                    <span className="flex flex-col items-center gap-1">
                      <FileUp className="w-5 h-5" />
                      Arraste um ficheiro ou clique para escolher
                      <span className="text-[10px] text-clay/70">GeoJSON, KML, KMZ, Shapefile (.zip), DXF</span>
                    </span>
                  )}
                </div>
              )}

              {draftGeometry && (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-white/[0.1] px-3 py-2 text-xs">
                  <span className="truncate text-limestone">{uploadedName ?? 'poligono carregado'}</span>
                  <button
                    type="button"
                    onClick={clearUpload}
                    className="shrink-0 rounded-lg border border-white/[0.1] px-2 py-1 text-clay hover:text-limestone"
                    aria-label="Remover"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}

              {uploadNotice && <p className="text-xs text-amber-300/90">{uploadNotice}</p>}
              {uploadError && <p className="text-xs text-red-300">{uploadError}</p>}
            </>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Picker — only meaningful once a polygon (area) exists */}
        {areaHa > 0 ? (
          <ProductPanel
            areaHa={areaHa}
            ctaLabel="Encomendar"
            submitting={submitting}
            disabled={!name.trim()}
            onAddToCart={placeOrder}
          />
        ) : (
          <p className="text-xs text-clay">
            Desenhe ou carregue o terreno para escolher as camadas e ver os precos.
          </p>
        )}

        {areaHa > 0 && !name.trim() && (
          <p className="text-xs text-clay">Indique um nome de projeto para encomendar.</p>
        )}
      </div>
    </div>
  )
}
